import React, { Suspense, lazy } from "react";
import { connect } from "react-redux";
import { HashRouter as Router, Route, Routes } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { isLoggedProps, LoggedDetailsType } from "../redux/UserInfo/IsLoggedInterfaces";
import FetchData from "./ServerCall/FetchData";
import { resetUser } from "../redux/UserInfo/isLoggedActions";

// Eager: small/critical routes needed on first paint or for the unauth path
import Login from "../pages/auth/login/Login";

// Lazy: heavier authenticated routes — split into separate chunks
const NewDataSet = lazy(() => import("./DataSet/NewDataSet"));
const EditDataSet = lazy(() => import("./DataSet/EditDataSet"));
const DataSetShow = lazy(() => import("./DataSet/DataSetShow"));
const DataViewer = lazy(() => import("./DataViewer/DataViewer"));
const FlatFileUpload = lazy(() => import("./DataConnection/FlatFileUpload"));
const EditFlatFileData = lazy(() => import("./DataConnection/EditFlatFileData"));
const NewDataConnection = lazy(() => import("./DataConnection/NewDataConnection"));
const AiSettingsList = lazy(() => import("./AISettings/AiSettingsList"));
const ModelPipelineDashboard = lazy(() => import("./ModelPipelines/Dashboard/ModelPipelineDashboard"));
const ModelPipelineDashboardShow = lazy(() => import("./ModelPipelines/Dashboard/ModelPipelineDashboardShow"));
const DigitalTwinPipelineDashboard = lazy(() => import("./DigitalTwinPipelines/Dashboard/DigitalTwinPipelineDashboard"));
const ModelSchedulesDashboard = lazy(() => import("./ModelSchedules/Dashboard/ModelSchedulesDashboard"));
const ModelSchedulesDashboardShow = lazy(() => import("./ModelSchedules/Dashboard/ModelSchedulesDashboardShow"));
const Dashboard = lazy(() => import("../pages/dashboard/main/Main"));
const MainDashboard = lazy(() => import("../pages/mainDashboard/MainDashboard"));
const ProjectList = lazy(() => import("../pages/projects/list"));
const ListDataSource = lazy(() => import("../pages/dataSource/list"));
const ListPipeline = lazy(() => import("../pages/pipeline/list"));
const ListDeployedModel = lazy(() => import("../pages/deployedModel/list"));
const ListPlaybook = lazy(() => import("../pages/playbook/list"));
const ListModelSchedule = lazy(() => import("../pages/modelSchedule/list"));
const ListDataset = lazy(() => import("../pages/dataset/list"));
const Profile = lazy(() => import("../pages/profile/Profile"));
const Settings = lazy(() => import("../pages/settings/Settings"));

const FIVE_MINUTES_MS = 5 * 60 * 1000;

const RouteFallback = () => (
	<div style={{ padding: 24, color: "#666" }}>Loading…</div>
);

const Home = (props: LoggedDetailsType) => {
	const dispatch = useDispatch();

	const shouldRefreshSession = () => {
		if (!props.loginTime) return true;
		const now = new Date().getTime();
		return now - props.loginTime > FIVE_MINUTES_MS;
	};

	if (shouldRefreshSession()) {
		FetchData({
			requestType: "auth",
			method: "GET",
			url: "me",
			headers: { "Content-Type": "application/json", Authorization: `Bearer ${props.accessToken}` },
		}).then((r) => {
			if (!r.status) {
				dispatch(resetUser());
			}
		});
	}

	return (
		<Router>
			<Suspense fallback={<RouteFallback />}>
				<Routes>
					{props.isUserLogged ? (
						<>
							<Route path="/newdataset" element={<NewDataSet />} />
							<Route path="/editdataset" element={<EditDataSet />} />
							<Route path="/dataset/:id" element={<DataSetShow />} />
							<Route path="/datahome_old" element={<Dashboard />} />
							<Route path="/datahome" element={<MainDashboard />} />
							<Route path="/datahome/:project_id" element={<MainDashboard />} />
							<Route path="/dataviewer" element={<DataViewer />} />
							<Route path="/flatfileupload" element={<FlatFileUpload />} />
							<Route path="/ai-settings" element={<AiSettingsList />} />
							<Route path="/editflatfile" element={<EditFlatFileData />} />
							<Route path="/newdataconnection" element={<NewDataConnection />} />
							<Route path="/model-pipeline" element={<ModelPipelineDashboard />} />
							<Route path="/digital-twin-pipeline" element={<DigitalTwinPipelineDashboard />} />
							<Route path="/model-schedules" element={<ModelSchedulesDashboard />} />
							<Route path="/model-schedules/:id" element={<ModelSchedulesDashboardShow />} />
							<Route path="/model-pipeline/:id" element={<ModelPipelineDashboardShow />} />
							<Route path="/list-data-source" element={<ListDataSource />} />
							<Route path="/list-pipeline" element={<ListPipeline />} />
							<Route path="/list-playbook" element={<ListPlaybook />} />
							<Route path="/list-model-schedule" element={<ListModelSchedule />} />
							<Route path="/list-dataset" element={<ListDataset />} />
							<Route path="/deployed-model-list" element={<ListDeployedModel />} />
							<Route path="/projects" element={<ProjectList />} />
							<Route path="/projects/:project_id" element={<Dashboard />} />
							<Route path="/projects/:project_id/editflatfile" element={<EditFlatFileData />} />
							<Route path="/projects/:project_id/flatfileupload" element={<FlatFileUpload />} />
							<Route path="/projects/:project_id/newdataset" element={<NewDataSet />} />
							<Route path="/projects/:project_id/model-pipeline" element={<ModelPipelineDashboard />} />
							<Route path="/projects/:project_id/digital-twin-pipeline" element={<DigitalTwinPipelineDashboard />} />
							<Route path="/projects/:project_id/newdataconnection" element={<NewDataConnection />} />
							<Route path="/projects/:project_id/model-schedules" element={<ModelSchedulesDashboard />} />
							<Route path="/projects/:project_id/dataviewer" element={<DataViewer />} />
							<Route path="/dashboard" element={<MainDashboard />} />
							<Route path="/profile" element={<Profile />} />
							<Route path="/settings" element={<Settings />} />
						</>
					) : (
						<Route path="/*" element={<Navigate to="/login" />} />
					)}
					<Route path="/" element={<Login />} />
					<Route path="/test" element={<Login />} />
					<Route path="/login" element={<Login />} />
				</Routes>
			</Suspense>
		</Router>
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapStateToProps = (state: isLoggedProps, _ownProps: any) => {
	return {
		isUserLogged: state.isLogged.isUserLogged,
		loginTime: state.isLogged.loginTime,
		accessToken: state.isLogged.accessToken,
	};
};

export default connect(mapStateToProps, null)(Home);
